import { type SVGAttributes, useMemo } from 'react';
import { cn } from '../../utils/cn';

export interface SparklineProps extends Omit<SVGAttributes<SVGSVGElement>, 'children'> {
  /** Numeric series — at least 2 points to render a line. */
  readonly data: readonly number[];
  readonly width?: number;
  readonly height?: number;
  readonly variant?: 'line' | 'area' | 'pos-neg';
  /** Override the accent colour token (default `--color-accent`). */
  readonly colorVar?: string;
  readonly strokeWidth?: number;
  readonly label?: string;
}

/**
 * Tiny inline trend chart. Used in Stat / KPICard / list rows. The result
 * scales to the SVG viewport so it's responsive when stretched. For
 * full-feature charts use the Chart component instead.
 */
export function Sparkline({
  data,
  width = 80,
  height = 28,
  variant = 'line',
  colorVar = '--color-accent',
  strokeWidth = 1.5,
  label,
  className,
  ...rest
}: SparklineProps) {
  const { linePath, areaPath, posPath, negPath, zeroY } = useMemo(() => {
    if (data.length < 2) {
      return { linePath: '', areaPath: '', posPath: '', negPath: '', zeroY: height };
    }
    const min = Math.min(...data);
    const max = Math.max(...data);
    const span = max - min || 1;
    const stepX = width / (data.length - 1);
    const ys = data.map(
      (v) => height - ((v - min) / span) * (height - strokeWidth * 2) - strokeWidth,
    );

    const line = data.map((_, i) => `${i === 0 ? 'M' : 'L'} ${i * stepX} ${ys[i]}`).join(' ');
    const area = `${line} L ${(data.length - 1) * stepX} ${height} L 0 ${height} Z`;

    // For pos-neg: split where the value crosses 0
    if (variant === 'pos-neg') {
      const yForZero =
        max <= 0
          ? 0
          : min >= 0
            ? height
            : height - ((0 - min) / span) * (height - strokeWidth * 2) - strokeWidth;
      let pos = '';
      let neg = '';
      for (let i = 0; i < data.length; i++) {
        const x = i * stepX;
        const y = ys[i]!;
        const cmd = i === 0 ? 'M' : 'L';
        if (data[i]! >= 0) pos += ` ${cmd} ${x} ${y}`;
        else neg += ` ${cmd} ${x} ${y}`;
      }
      return {
        linePath: line,
        areaPath: area,
        posPath: pos.trim(),
        negPath: neg.trim(),
        zeroY: yForZero,
      };
    }

    return { linePath: line, areaPath: area, posPath: '', negPath: '', zeroY: height };
  }, [data, width, height, strokeWidth, variant]);

  if (data.length < 2) return null;
  const stroke = `var(${colorVar})`;

  return (
    <svg
      role={label ? 'img' : 'presentation'}
      aria-label={label}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={cn('overflow-visible', className)}
      {...rest}
    >
      {variant === 'area' ? (
        <>
          <path d={areaPath} fill={stroke} fillOpacity={0.18} />
          <path
            d={linePath}
            fill="none"
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ) : variant === 'pos-neg' ? (
        <>
          <line
            x1={0}
            x2={width}
            y1={zeroY}
            y2={zeroY}
            stroke="var(--color-hairline-2)"
            strokeWidth={0.5}
          />
          {posPath ? (
            <path
              d={posPath}
              fill="none"
              stroke="var(--color-success)"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : null}
          {negPath ? (
            <path
              d={negPath}
              fill="none"
              stroke="var(--color-danger)"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : null}
        </>
      ) : (
        <path
          d={linePath}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}
